import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'courdowner', label: 'Court Owner' },
  { value: 'player', label: 'Player' },
];

export default function Register() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'courdowner',
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phoneNumber, password, confirmPassword, role } = form;

    if (!firstName || !lastName || !email || !password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/auth/register', {
        firstName,
        lastName,
        email,
        phoneNumber,
        password,
        role,
      });
      toast.success('Account created successfully! Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img
            src="/Logo.png"
            alt="FREAC"
            className="mx-auto w-40 h-40 rounded-3xl object-cover mb-4
                       shadow-[0_12px_32px_rgba(25,211,230,.15)]"
          />
          <h1 className="text-2xl font-bold text-[#063B46]">Create Account</h1>
          <p className="text-[#667085] mt-1 text-sm">Set up your FREAC admin access</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-[20px] border border-[#EAECF0] p-8 space-y-5 shadow-[0_6px_18px_rgba(0,0,0,.06)]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="admin-label">First Name *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={update('firstName')}
                className="admin-input"
                placeholder="John"
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className="admin-label">Last Name *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={update('lastName')}
                className="admin-input"
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div>
            <label className="admin-label">Email Address *</label>
            <input
              type="email"
              value={form.email}
              onChange={update('email')}
              className="admin-input"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="admin-label">Phone Number</label>
            <input
              type="tel"
              value={form.phoneNumber}
              onChange={update('phoneNumber')}
              className="admin-input"
              placeholder="+63 912 345 6789"
              autoComplete="tel"
            />
          </div>

          <div>
            <label className="admin-label">Account Type *</label>
            <select
              value={form.role}
              onChange={update('role')}
              className="admin-input"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="admin-label">Password *</label>
              <input
                type="password"
                value={form.password}
                onChange={update('password')}
                className="admin-input"
                placeholder="Min 6 characters"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="admin-label">Confirm Password *</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={update('confirmPassword')}
                className="admin-input"
                placeholder="Repeat password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="admin-btn-primary w-full"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>

          <p className="text-center text-sm text-[#667085]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#19D3E6] hover:text-[#00B8D9] font-semibold transition-colors">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
